import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { NFConferenciaTable } from './NFConferenciaTable';
import { PdfViewer, type PdfEntry } from './PdfViewer';
import { ConfirmDialog } from './ConfirmDialog';
import { fmtDate, fmtMoney2 } from '../lib/fmt';
import { maskCNPJ } from '../lib/masks';
import { MoneyInput } from './inputs/MoneyInput';
import { CnpjInput } from './inputs/CnpjInput';
import { dateDiffDays } from '../lib/dateHelpers';
import { readBoletosReais, type BoletoReal, type FaturaNF } from '../lib/nfStatus';
import {
  extractNfPdfText,
  padPedido,
  parseNotaFiscalPDF,
  type NFKind,
} from '../lib/nfPdfParser';
import { fileToPdfMeta } from '../lib/pdf';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { Divergencia, NFConferenciaPreview, NFIn, NFOut, ParcelaOut, PdfMetaNF, PedidoOut } from '../types/api';

type PdfMeta = PdfMetaNF;

interface NFFormProps {
  pedidos: PedidoOut[];
  existingNF?: NFOut | null;
  suggestedPedidoId?: number | null;
  /** No hub do pedido a NF está sempre atrelada a um pedido comum: esconde tipo e seletor de pedido. */
  lockPedido?: boolean;
  onSaved?: (nf: NFOut) => void;
  onDeleted?: (id: number) => void;
  onCancel: () => void;
}

function getPedidoStatus(nf: { tipo_nf: NFKind; pedido_id: number | null; divergencias: Divergencia[] }) {
  if (nf.tipo_nf === 'bonificacao') return 'bonificacao';
  if (!nf.pedido_id) return 'pendente';
  const hasHigh = nf.divergencias.some(d => String(d.sev ?? '') === 'alta');
  return hasHigh ? 'divergente' : 'conferido';
}

export function NFForm({
  pedidos,
  existingNF,
  suggestedPedidoId,
  lockPedido = false,
  onSaved,
  onDeleted,
  onCancel,
}: NFFormProps) {
  const qc = useQueryClient();
  const isEditing = Boolean(existingNF);
  const suggestedPedido = pedidos.find(p => p.id === suggestedPedidoId) ?? null;

  const [tipoNF, setTipoNF] = useState<NFKind>(existingNF?.tipo_nf ?? 'comum');
  const [pedidoId, setPedidoId] = useState<number | null>(existingNF?.pedido_id ?? suggestedPedidoId ?? null);
  const [numero, setNumero] = useState(existingNF?.numero ?? '');
  const [serie, setSerie] = useState(existingNF?.serie ?? '');
  const [emitente, setEmitente] = useState(existingNF?.emitente ?? suggestedPedido?.fornecedor ?? '');
  const [cnpjEmitente, setCnpjEmitente] = useState(existingNF?.cnpj_emitente ?? '');
  const [dataEmissao, setDataEmissao] = useState(existingNF?.data_emissao?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
  const [dataRecebimento, setDataRecebimento] = useState(existingNF?.data_recebimento?.slice(0, 10) ?? suggestedPedido?.data_recebimento?.slice(0, 10) ?? '');
  const [dataCompetencia, setDataCompetencia] = useState(existingNF?.data_competencia?.slice(0, 10) ?? '');
  const [valorTotal, setValorTotal] = useState<number>(
    existingNF ? Number(existingNF.valor_total) || 0 : Number(suggestedPedido?.valor_total) || 0,
  );
  const [pedidoNumeroRef, setPedidoNumeroRef] = useState('');
  const [pdfMeta, setPdfMeta] = useState<PdfMeta | null>(existingNF?.pdf_meta ?? null);
  const [chaveAcesso, setChaveAcesso] = useState(existingNF?.pdf_meta?.chaveAcesso ?? '');
  const [naturezaOperacao, setNaturezaOperacao] = useState(existingNF?.pdf_meta?.naturezaOperacao ?? '');
  const [valorProdutos, setValorProdutos] = useState<number | null>(existingNF?.pdf_meta?.valorProdutos ?? null);
  const [valorIPI, setValorIPI] = useState<number | null>(existingNF?.pdf_meta?.valorIPI ?? null);
  const [valorICMS, setValorICMS] = useState<number | null>(existingNF?.pdf_meta?.valorICMS ?? null);
  const [faturas, setFaturas] = useState<FaturaNF[]>(
    (existingNF?.faturas ?? []).map((raw, idx) => ({
      n: Number(raw.n ?? idx + 1),
      vencimento: String(raw.vencimento ?? ''),
      valor: Number(raw.valor ?? 0),
    })).filter(f => f.vencimento),
  );
  const [boletosReais, setBoletosReais] = useState<BoletoReal[]>(readBoletosReais(existingNF?.pdf_meta));
  const [pdfError, setPdfError] = useState('');
  const [confirmRemovePdf, setConfirmRemovePdf] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const pedido = useMemo(() => pedidos.find(p => p.id === pedidoId) ?? null, [pedidoId, pedidos]);
  const { data: parcelasPedido = [] } = useQuery<ParcelaOut[]>({
    queryKey: ['pedido-parcelas-nf', pedidoId],
    enabled: Boolean(pedidoId && tipoNF !== 'bonificacao'),
    queryFn: () => api.get<ParcelaOut[]>(`/pedidos/${pedidoId}/parcelas`).then(r => r.data),
  });
  // A conferência (tabela + divergências + condição) é calculada no backend.
  // O front monta um rascunho e consulta POST /nfs/conferencia com debounce.
  const conferenciaReq = useMemo(() => ({
    tipo_nf: tipoNF,
    pedido_id: tipoNF === 'bonificacao' ? null : pedidoId,
    emitente,
    valor_total: valorTotal || 0,
    data_emissao: dataEmissao || null,
    data_competencia: dataCompetencia || null,
    faturas: faturas.map(f => ({ n: f.n, vencimento: f.vencimento, valor: f.valor })),
    boletos_reais: boletosReais,
    pedido_numero_ref: pedidoNumeroRef,
  }), [tipoNF, pedidoId, emitente, valorTotal, dataEmissao, dataCompetencia, faturas, boletosReais, pedidoNumeroRef]);
  const debouncedReq = useDebouncedValue(conferenciaReq, 400);

  const { data: preview, isFetching: previewLoading } = useQuery<NFConferenciaPreview>({
    queryKey: ['nf-conferencia', debouncedReq],
    enabled: tipoNF !== 'bonificacao',
    placeholderData: prev => prev,
    queryFn: () => api.post<NFConferenciaPreview>('/nfs/conferencia', debouncedReq).then(r => r.data),
  });

  const conferenciaRows = preview?.rows ?? [];
  const divergencias = preview?.divergencias ?? [];
  const condicaoExtraida = preview?.condicaoExtraida ?? '';
  const competenciaAjustes = preview?.competenciaAjustes ?? [];
  const statusConferencia = preview?.status ?? 'pendente';

  const leadTimeDias = useMemo(() => {
    if (!dataEmissao || !dataRecebimento) return null;
    return dateDiffDays(dataEmissao, dataRecebimento);
  }, [dataEmissao, dataRecebimento]);

  useEffect(() => {
    if (!pedido) return;
    if (!emitente.trim()) setEmitente(pedido.fornecedor);
  }, [emitente, pedido]);

  const saveMut = useMutation({
    mutationFn: (payload: NFIn) => {
      if (isEditing && existingNF) return api.put<NFOut>(`/nfs/${existingNF.id}`, payload).then(r => r.data);
      return api.post<NFOut>('/nfs', payload).then(r => r.data);
    },
    onSuccess: nf => {
      qc.invalidateQueries({ queryKey: ['nfs'] });
      onSaved?.(nf);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/nfs/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['nfs'] });
      onDeleted?.(id);
    },
  });

  const removePdfMut = useMutation({
    mutationFn: (id: number) => api.delete(`/nfs/${id}/pdf`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nfs'] });
    },
  });

  function doRemovePdf() {
    setPdfMeta(null);
    setChaveAcesso('');
    setNaturezaOperacao('');
    setValorProdutos(null);
    setValorIPI(null);
    setValorICMS(null);
    if (fileRef.current) fileRef.current.value = '';
    if (isEditing && existingNF?.pdf_meta?.has_pdf) removePdfMut.mutate(existingNF.id);
    setConfirmRemovePdf(false);
  }

  const status = tipoNF === 'bonificacao'
    ? 'bonificacao'
    : statusConferencia === 'conferido' || statusConferencia === 'divergente'
      ? statusConferencia
      : getPedidoStatus({ tipo_nf: tipoNF, pedido_id: pedidoId, divergencias });
  const statusBadge = status === 'conferido'
    ? 'bg-emerald-500/20 text-emerald-300'
    : status === 'divergente'
      ? 'bg-rose-500/20 text-rose-300'
      : status === 'bonificacao'
        ? 'bg-emerald-500/15 text-emerald-300'
        : 'bg-amber-500/20 text-amber-300';

  async function handlePdf(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfError('');
    try {
      const extracted = await extractNfPdfText(file);
      const parsed = parseNotaFiscalPDF(extracted);
      if (parsed.numero) setNumero(parsed.numero);
      if (parsed.serie) setSerie(parsed.serie);
      if (parsed.emitente) setEmitente(parsed.emitente);
      if (parsed.cnpjEmitente) setCnpjEmitente(maskCNPJ(parsed.cnpjEmitente));
      if (parsed.dataEmissao) setDataEmissao(parsed.dataEmissao);
      if (parsed.valorTotal != null) setValorTotal(parsed.valorTotal);
      if (parsed.faturas.length) {
        setFaturas(parsed.faturas);
        setBoletosReais([]);
      }
      setPedidoNumeroRef(parsed.pedidoNumero ?? '');
      if (!lockPedido) setTipoNF(parsed.tipoNF);
      setChaveAcesso(parsed.chaveAcesso ?? '');
      setNaturezaOperacao(parsed.naturezaOperacao ?? '');
      setValorProdutos(parsed.valorProdutos);
      setValorIPI(parsed.valorIPI);
      setValorICMS(parsed.valorICMS);
      setPdfMeta(await fileToPdfMeta(file, {
        ...(parsed.chaveAcesso ? { chaveAcesso: parsed.chaveAcesso } : {}),
        ...(parsed.naturezaOperacao ? { naturezaOperacao: parsed.naturezaOperacao } : {}),
        ...(parsed.valorProdutos != null ? { valorProdutos: parsed.valorProdutos } : {}),
        ...(parsed.valorIPI != null ? { valorIPI: parsed.valorIPI } : {}),
        ...(parsed.valorICMS != null ? { valorICMS: parsed.valorICMS } : {}),
      }));
      if (!lockPedido && parsed.pedidoNumero && parsed.tipoNF !== 'bonificacao') {
        const found = pedidos.find(p => padPedido(p.numero) === padPedido(parsed.pedidoNumero));
        if (found) setPedidoId(found.id);
      }
      if (!parsed.numero && !parsed.emitente && parsed.valorTotal == null) {
        setPdfError('PDF anexado, mas sem campos reconhecíveis. Complete manualmente.');
      }
    } catch {
      setPdfError('Não foi possível ler o PDF. O preenchimento manual continua disponível.');
    }
  }

  function updateBoletoReal(idx: number, field: keyof BoletoReal, value: string) {
    setBoletosReais(current => {
      const next = [...current];
      const row = { ...(next[idx] ?? {}) };
      if (field === 'valor') row.valor = value;
      else row.data = value;
      next[idx] = row;
      return next;
    });
  }

  function handleSubmit() {
    if (!numero.trim()) return;
    if (!emitente.trim()) return;
    if (tipoNF !== 'bonificacao' && !pedidoId) return;
    // Só os dados informados (data/valor) são persistidos; status/diff são recalculados
    // de forma autoritativa pelo backend (services/nf_conferencia.py).
    const boletosReaisPayload = conferenciaRows.map(row => ({
      data: row.realData || undefined,
      valor: row.realValor ?? undefined,
    }));
    const payload: NFIn = {
      numero: numero.trim(),
      serie: serie.trim() || undefined,
      emitente: emitente.trim(),
      cnpj_emitente: cnpjEmitente.trim() || undefined,
      data_emissao: dataEmissao,
      data_recebimento: dataRecebimento || undefined,
      data_competencia: dataCompetencia || undefined,
      valor_total: valorTotal || 0,
      condicao_extraida: condicaoExtraida || undefined,
      tipo_nf: tipoNF,
      lead_time_dias: leadTimeDias ?? undefined,
      pedido_id: tipoNF === 'bonificacao' ? undefined : pedidoId ?? undefined,
      divergencias,
      faturas: faturas.map(f => ({ n: f.n, vencimento: f.vencimento, valor: f.valor })),
      // O backend recalcula divergencias/statusConferencia de forma autoritativa
      // (services/nf_conferencia.py). pedidoNumeroRef é persistido no pdf_meta para que
      // o servidor consiga reproduzir a divergência de "Pedido referenciado divergente".
      pdf_meta: pdfMeta
        ? { ...pdfMeta, boletosReais: boletosReaisPayload, statusConferencia, pedidoNumeroRef }
        : { boletosReais: boletosReaisPayload, statusConferencia, pedidoNumeroRef },
    };
    saveMut.mutate(payload);
  }

  const errorDetail = (saveMut.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail
    ?? (deleteMut.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail;
  const totalPedidoParcelas = parcelasPedido.reduce((sum, item) => sum + Number(item.valor), 0);
  const totalFaturas = faturas.reduce((sum, item) => sum + item.valor, 0);
  const totalBoletoReal = conferenciaRows.reduce((sum, row) => sum + (row.realData && row.realValor != null ? row.realValor : 0), 0);

  const hasPdfOnServer = Boolean(existingNF?.pdf_meta?.has_pdf);
  const serverPdfEntries: PdfEntry[] = hasPdfOnServer && existingNF
    ? [{ key: `nf-${existingNF.id}`, label: `NF ${existingNF.numero}`, path: `/nfs/${existingNF.id}/pdf`, fileName: `nf_${existingNF.numero}.pdf`, nfId: existingNF.id }]
    : [];
  const showTipo = !lockPedido;
  const showPedidoSelect = !lockPedido && tipoNF !== 'bonificacao';

  return (
    <div>
      {showTipo && (
        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <label className={`card cursor-pointer p-3 ${tipoNF === 'comum' ? 'border-mbc-orange' : 'border-mbc-line'}`}>
            <input type="radio" name="nf-tipo" value="comum" checked={tipoNF === 'comum'} onChange={() => setTipoNF('comum')} className="accent-mbc-orange" />
            <div className="mt-2">
              <div className="text-sm font-bold text-white">📄 NF Comum</div>
              <div className="text-[10px] text-mbc-mute">Compara com pedido aprovado.</div>
            </div>
          </label>
          <label className={`card cursor-pointer p-3 ${tipoNF === 'bonificacao' ? 'border-emerald-400' : 'border-mbc-line'}`}>
            <input type="radio" name="nf-tipo" value="bonificacao" checked={tipoNF === 'bonificacao'} onChange={() => setTipoNF('bonificacao')} className="accent-emerald-400" />
            <div className="mt-2">
              <div className="text-sm font-bold text-emerald-300">🎁 Bonificação</div>
              <div className="text-[10px] text-mbc-mute">Agrega por fornecedor e não exige pedido.</div>
            </div>
          </label>
        </div>
      )}

      <div className="mb-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-mbc-mute">📎 PDF da NF</label>
        <label className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-mbc-line py-3 text-sm text-mbc-soft transition hover:border-mbc-orange">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handlePdf} />
          <span>📎 Selecionar PDF para preenchimento automático</span>
        </label>
        {pdfMeta ? (
          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="text-xs text-emerald-400">
              ✓ {pdfMeta.name ?? 'Dados de conferência salvos'}{pdfMeta.size ? ` · ${(pdfMeta.size / 1024).toFixed(1)} KB` : ''}
            </p>
            <button type="button" onClick={() => setConfirmRemovePdf(true)} className="rounded bg-rose-500/15 px-2.5 py-1 text-xs text-rose-300 hover:bg-rose-500/30">
              🗑️ Remover PDF
            </button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-mbc-mute">Nenhum arquivo anexado.</p>
        )}
        {serverPdfEntries.length > 0 && (
          <div className="mt-2">
            <PdfViewer entries={serverPdfEntries} onRemove={() => setConfirmRemovePdf(true)} removing={removePdfMut.isPending} />
          </div>
        )}
        {pdfError && <p className="mt-1 text-xs text-amber-300">{pdfError}</p>}
        {(chaveAcesso || naturezaOperacao || valorProdutos != null || valorIPI != null || valorICMS != null) && (
          <div className="mt-3 rounded-xl border border-mbc-line bg-mbc-panel p-3 space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-mbc-mute mb-2">Dados fiscais extraídos do DANFE</div>
            {chaveAcesso && (
              <div className="flex items-start gap-2">
                <span className="text-[10px] text-mbc-mute w-32 shrink-0">Chave de acesso</span>
                <span className="text-[10px] font-mono text-mbc-soft break-all">{chaveAcesso}</span>
              </div>
            )}
            {naturezaOperacao && (
              <div className="flex items-start gap-2">
                <span className="text-[10px] text-mbc-mute w-32 shrink-0">Natureza da op.</span>
                <span className="text-[10px] text-mbc-soft">{naturezaOperacao}</span>
              </div>
            )}
            {(valorProdutos != null || valorIPI != null || valorICMS != null) && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1">
                {valorProdutos != null && (
                  <div>
                    <span className="text-[10px] text-mbc-mute">Produtos </span>
                    <span className="text-[10px] font-semibold text-mbc-soft">{fmtMoney2(valorProdutos)}</span>
                  </div>
                )}
                {valorIPI != null && (
                  <div>
                    <span className="text-[10px] text-mbc-mute">IPI </span>
                    <span className="text-[10px] font-semibold text-mbc-soft">{fmtMoney2(valorIPI)}</span>
                  </div>
                )}
                {valorICMS != null && (
                  <div>
                    <span className="text-[10px] text-mbc-mute">ICMS </span>
                    <span className="text-[10px] font-semibold text-mbc-soft">{fmtMoney2(valorICMS)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        {showPedidoSelect && (
          <div className="md:col-span-2">
            <label className="text-xs text-mbc-mute">Pedido associado *</label>
            <select value={pedidoId ?? ''} onChange={e => setPedidoId(e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none">
              <option value="">Selecione um pedido aprovado</option>
              {pedidos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.numero} · {p.fornecedor} · {fmtMoney2(Number(p.valor_total))}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="text-xs text-mbc-mute">Número da NF *</label>
          <input value={numero} onChange={e => setNumero(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Série</label>
          <input value={serie} onChange={e => setSerie(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Emitente *</label>
          <input value={emitente} onChange={e => setEmitente(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">CNPJ</label>
          <CnpjInput value={cnpjEmitente} onChange={setCnpjEmitente} />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Data de emissão</label>
          <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Data de recebimento</label>
          <input type="date" value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Data de competência</label>
          <input type="date" value={dataCompetencia} onChange={e => setDataCompetencia(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
          <p className="mt-1 text-[10px] text-mbc-mute">Mês em que o fornecedor contabiliza — deixe em branco para usar a emissão.</p>
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Valor total (R$)</label>
          <MoneyInput value={valorTotal} onChange={setValorTotal} />
        </div>
        <div>
          <label className="text-xs text-mbc-mute">Condição extraída</label>
          <input value={condicaoExtraida} readOnly className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel px-3 py-2 text-sm text-mbc-soft focus:outline-none" />
        </div>
      </div>

      {tipoNF !== 'bonificacao' && (
        <NFConferenciaTable
          pedido={pedido}
          parcelasPedidoCount={parcelasPedido.length}
          faturasCount={faturas.length}
          rows={conferenciaRows}
          statusConferencia={statusConferencia}
          totalPedidoParcelas={totalPedidoParcelas}
          totalFaturas={totalFaturas}
          totalBoletoReal={totalBoletoReal}
          onBoletoRealChange={updateBoletoReal}
          competenciaAjustes={competenciaAjustes}
        />
      )}
      <div className="grid gap-4 lg:grid-cols-[1.1fr_.9fr]">
        <div className="card p-4" style={{ background: 'rgba(10,77,158,.08)', borderColor: 'rgba(30,111,214,.35)' }}>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-mbc-bluel">📅 Faturas extraídas</div>
            <span className="text-xs text-mbc-mute">{faturas.length} parcela(s)</span>
          </div>
          {faturas.length === 0 ? (
            <p className="py-3 text-center text-xs text-mbc-mute">Anexe o PDF da NF para extrair as faturas automaticamente.</p>
          ) : (
            <div className="overflow-auto">
              <table className="dt" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th className="text-center">Parc.</th>
                    <th>Vencimento</th>
                    <th className="num">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {faturas.map(f => (
                    <tr key={`${f.n}-${f.vencimento}-${f.valor}`}>
                      <td className="text-center font-bold">{f.n}</td>
                      <td>{fmtDate(f.vencimento)}</td>
                      <td className="num">{fmtMoney2(f.valor)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #22324F' }}>
                    <td colSpan={2} className="text-right text-mbc-mute text-xs">Total:</td>
                    <td className="num font-bold text-white">{fmtMoney2(faturas.reduce((sum, item) => sum + item.valor, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-4" style={{ background: 'rgba(244,122,32,.05)', borderColor: 'rgba(244,122,32,.3)' }}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-mbc-orangel">
                🔎 Status da conferência{previewLoading && <span className="ml-2 font-normal text-mbc-mute normal-case">· calculando…</span>}
              </div>
              <span className={`badge ${statusBadge}`}>
                {status === 'conferido' ? 'Conferido' : status === 'divergente' ? 'Com divergência' : status === 'bonificacao' ? 'Bonificação' : 'Pendente'}
              </span>
            </div>
            {tipoNF !== 'bonificacao' && pedido ? (
              <div className="text-xs text-mbc-soft">
                <div>Pedido: <b className="text-white">{pedido.numero}</b></div>
                <div>Fornecedor: <b className="text-white">{pedido.fornecedor}</b></div>
                <div>Valor do pedido: <b className="text-white">{fmtMoney2(Number(pedido.valor_total))}</b></div>
                <div>Condição acordada: <b className="text-white">{pedido.condicao ?? '—'}</b></div>
              </div>
            ) : (
              <p className="text-xs text-mbc-soft">Notas de bonificação não são confrontadas com pedido.</p>
            )}
            {leadTimeDias != null && (
              <p className="mt-3 text-xs text-mbc-soft">Lead time real: <b className="text-white">{leadTimeDias} dia(s)</b></p>
            )}
          </div>

          <div className="card p-4" style={{ background: divergencias.length ? 'rgba(244,63,94,.05)' : 'rgba(34,160,107,.08)', borderColor: divergencias.length ? 'rgba(244,63,94,.3)' : 'rgba(34,160,107,.4)' }}>
            <div className={`mb-2 font-bold text-sm ${divergencias.length ? 'text-rose-300' : 'text-emerald-300'}`}>
              {divergencias.length ? `⚠ ${divergencias.length} divergência(s)` : '✓ Nenhuma divergência detectada'}
            </div>
            {divergencias.length ? (
              <div className="space-y-2">
                {divergencias.map((div, idx) => (
                  <div key={`${div.titulo}-${idx}`} className="rounded-lg border border-white/5 bg-black/10 p-2">
                    <div className="text-sm font-semibold text-white">{String(div.titulo ?? 'Divergência')}</div>
                    <div className="text-xs text-mbc-soft mt-0.5">{String(div.detalhe ?? '')}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-mbc-soft">A NF está consistente com o pedido selecionado.</p>
            )}
          </div>
        </div>
      </div>

      {errorDetail && <p className="mt-4 text-sm text-rose-300">{errorDetail}</p>}

      <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {isEditing && existingNF && (
          <button type="button" onClick={() => setConfirmDelete(true)} disabled={deleteMut.isPending} className="rounded-xl bg-rose-500/20 px-4 py-2.5 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40 w-full sm:mr-auto sm:w-auto">
            {deleteMut.isPending ? 'Excluindo…' : '🗑️ Excluir NF'}
          </button>
        )}
        <button type="button" onClick={onCancel} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-5 py-2.5 text-mbc-soft hover:text-white w-full sm:w-auto">
          Cancelar
        </button>
        <button type="button" onClick={handleSubmit} disabled={saveMut.isPending || !numero.trim() || !emitente.trim() || (tipoNF !== 'bonificacao' && !pedidoId)} className="pill-btn rounded-xl bg-mbc-orange px-6 py-2.5 font-bold text-white shadow-lg hover:bg-mbc-orangel disabled:opacity-40 w-full sm:w-auto">
          {saveMut.isPending ? 'Salvando…' : '💾 Salvar conferência'}
        </button>
      </div>

      {confirmRemovePdf && (
        <ConfirmDialog
          title="Remover PDF"
          message="Remover o PDF anexado? Os campos extraídos do arquivo serão limpos."
          confirmLabel="Remover"
          busy={removePdfMut.isPending}
          onConfirm={doRemovePdf}
          onCancel={() => setConfirmRemovePdf(false)}
        />
      )}
      {confirmDelete && existingNF && (
        <ConfirmDialog
          title="Excluir NF"
          message={`Excluir NF ${existingNF.numero}? Esta ação não pode ser desfeita.`}
          confirmLabel="Excluir"
          busy={deleteMut.isPending}
          onConfirm={() => { deleteMut.mutate(existingNF.id); setConfirmDelete(false); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
